import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { router } from "@inertiajs/react";
import { PagyMeta } from "@/types/pagy";

export default function Pagination({ pagy, basePath }: { pagy: PagyMeta; basePath: string }) {
  const page: number = pagy?.page ?? 1;
  const pages: number = pagy?.pages ?? 1;

  const goto = (p: number) => {
    if (p < 1 || p > pages) return;
    router.get(basePath, { page: p }, { replace: true });
  };

  if (pages <= 1) return null;

  const siblings = 1;

  const visiblePages = (): (number | "ellipsis")[] => {
    if (pages <= 5 + siblings * 2) {
      return Array.from({ length: pages }, (_, i) => i + 1);
    }

    let start = Math.max(2, page - siblings);
    let end = Math.min(pages - 1, page + siblings);

    if (start <= 2) {
      end = Math.min(pages - 1, end + (2 - start));
      start = 2;
    }

    if (end >= pages - 1) {
      start = Math.max(2, start - (end - (pages - 1)));
      end = pages - 1;
    }

    const showLeftEllipsis = start > 2;
    const showRightEllipsis = end < pages - 1;

    const range: (number | "ellipsis")[] = [1];

    if (showLeftEllipsis) range.push("ellipsis");

    for (let p = start; p <= end; p += 1) {
      range.push(p);
    }

    if (showRightEllipsis) range.push("ellipsis");

    range.push(pages);

    return range;
  };

  return (
    <div className="flex items-center justify-center space-x-1 mt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => goto(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>

      {visiblePages().map((value, idx) => {
        if (typeof value === "string") {
          return (
            <div key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">
              <MoreHorizontal className="h-4 w-4" />
            </div>
          );
        }

        const pageNumber = value as number;

        return (
          <Button
            key={pageNumber}
            variant={pageNumber === page ? "default" : "ghost"}
            size="sm"
            onClick={() => goto(pageNumber)}
            aria-current={pageNumber === page ? "page" : undefined}
          >
            {pageNumber}
          </Button>
        );
      })}

      <Button
        variant="outline"
        size="sm"
        onClick={() => goto(page + 1)}
        disabled={page >= pages}
        aria-label="Next page"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
    </div>
  );
}
